import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-client';
import { refreshOutreachStages } from './contact-book';
import { invalidateAgentTaskQueries, settleDecisionInAgentTaskCaches } from './agent-task-invalidation';
import type { WebLedgerDecision } from './goal-ledger';
import {
  answerSignature,
  buildVerdictBody,
  castDecisionVerdict,
  decisionOutcomeEffect,
  forgetVerdictPin,
  holdPlanVersion,
  isAnswerRecorded,
  mintVerdictId,
  pinVerdictId,
  readVerdictPin,
  settledDecisionView,
  writeVerdictPin,
  type DecisionAnswer,
  type DecisionOutcomeEffect,
  type PlanVersionHold,
  type VerdictPin,
} from './decisions';

/**
 * One open decision's answering state — busy, what came back, and the two values that must survive it.
 *
 * **Per ROW, not per section.** Two questions get answered seconds apart, and a flag shared across the
 * block greys out a row the reader has not touched (`PendingShares` learned this on all three surfaces).
 * A hook instance per row is that keying, without a map to keep in step.
 *
 * The two refs are the whole reason this is a hook rather than three `useState`s:
 *
 * - **The plan-version hold is taken on the FIRST render of this decision** and never re-read. The
 *   ledger refetches on every task write, so `decision.planVersion` moves under an open row; answering
 *   against the moved one compares the plan with itself and passes every time, which removes the
 *   staleness refusal entirely while leaving the code looking correct.
 * - **The verdict pin survives a failure**, because that is what makes a retry a retry. Same answer,
 *   same id — the service recognises its own record and reports `ALREADY_RECORDED` rather than voting
 *   twice. A different answer gets a new id, because it is a different tap.
 *
 * **Nothing is written optimistically.** The row switches to its settled sentence only on `ok: true`,
 * from the entity the response carried; every other result leaves the question open and says what
 * happened. An optimistic settle would render "you approved it" for a question that expired.
 */
export interface DecisionAnswerFlow {
  /** Which control is in flight, or null. The pressed one spins; its siblings disable. */
  busy: string | null;
  /** The settled row to render in place of the controls, once an answer is on file. */
  answered: WebLedgerDecision | null;
  /** What the last result means for the row — its line, its tone, and whether pressing again can help. */
  effect: DecisionOutcomeEffect | null;
  /** Commit an answer. `control` is only the id of the button that spins. */
  submit: (control: string, answer: DecisionAnswer) => Promise<void>;
}

export function useDecisionAnswer(
  decision: WebLedgerDecision,
  goalId: string | undefined,
  parentGoalId: string | undefined,
  /** The task this row is answering FOR, when the row is a task's own — the drill-in reads this exact
   *  question off `['task-detail', taskId]`, a cache the ledger's re-read never touches. Absent from the
   *  ledger's own call, which has no single task to invalidate. */
  taskId?: string,
  /** The outreach thread the question was raised on (`Decision.outreach_id`). Present → the contact book
   *  is re-read after the answer, so the thread can leave Needs you; absent → it is not touched (rule 12). */
  outreachId?: string,
): DecisionAnswerFlow {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [answered, setAnswered] = useState<WebLedgerDecision | null>(null);
  const [effect, setEffect] = useState<DecisionOutcomeEffect | null>(null);

  // Taken during RENDER, so the version pinned is the one the reader is looking at — not whatever the
  // next refetch replaces it with a second before the tap. See the note above.
  const holdRef = useRef<PlanVersionHold | null>(null);
  holdRef.current = holdPlanVersion(holdRef.current, decision);
  const pinRef = useRef<VerdictPin | null>(null);
  const busyRef = useRef(false);

  const submit = useCallback(
    async (control: string, answer: DecisionAnswer) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(control);
      setEffect(null);
      const hold = holdRef.current as PlanVersionHold;
      // Seeded from storage on the first press of this row, because a RELOAD is this surface's process
      // death and the retry that needs the old pin is the one after a response that never came back.
      // Same key and value format both phones write, so the rule is one rule and not three.
      const pin = pinVerdictId(pinRef.current ?? readVerdictPin(decision.id), answerSignature(answer), mintVerdictId);
      pinRef.current = pin;
      writeVerdictPin(decision.id, pin);
      const result = await castDecisionVerdict(buildVerdictBody({ decisionId: decision.id, verdictId: pin.verdictId, answer, hold }));
      if (isAnswerRecorded(result)) {
        // Spent: a further press would be a genuinely new answer against a row that is no longer open.
        // Kept on every FAILURE, because the pin is what makes a retry a retry rather than a second vote.
        forgetVerdictPin(decision.id);
        const returned = result.kind === 'outcome' ? result.response.decision : undefined;
        setAnswered(settledDecisionView(decision, answer, returned));
      } else {
        const outcome = result.kind === 'outcome' ? result.response.outcome : result.kind;
        setEffect(decisionOutcomeEffect(outcome));
      }
      busyRef.current = false;
      setBusy(null);
      const needsReread = isAnswerRecorded(result) || (result.kind === 'outcome' && decisionOutcomeEffect(result.response.outcome).reread);
      if (needsReread) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.goalLedger(goalId, parentGoalId) });
        // The plan is where an unblocked step becomes visible. Once, immediately — the release is async
        // and unbounded, and polling for it would put a spinner over a plan that is already true.
        // Every cache holding this task, from the one shared set — not a hand-picked pair. Answering a
        // question moves the row OUT of Action needed, so the workspace counts and the in-chat at-work
        // card are as stale as the list and the sheet; this used to invalidate only the latter two.
        // BEFORE the invalidation, and that order is the whole point: settling writes
        // `blockedByDecisions` and never the task's status, so the refetch the next line kicks off comes
        // back with the badge the row already had. Patching first is what makes the press visible on the
        // spot; the refetch then reconciles whatever the server really thinks.
        // The tapped option rides along ONLY when the answer is on file: a re-read for STALE_PLAN / ALREADY_SETTLED /
        // EXPIRED / WITHDRAWN / CLOSED recorded nothing, so its chip promises no status, and holding one would keep
        // the card off Action needed for 30 s while the server never moves it. Both phones gate it the same way.
        settleDecisionInAgentTaskCaches(
          decision.id,
          isAnswerRecorded(result) && answer.resolution === 'ACCEPTED' ? answer.choice : undefined,
        );
        invalidateAgentTaskQueries(taskId);
        // And the surface the question may have been ASKED on. The brief's own list is keyed by
        // conversation, and its only other refresh is the agent-task duplexer — so a decision whose
        // settle releases no task (or whose release is delayed; it is async and unbounded) would leave
        // the answered question sitting on the card with live controls under it. Invalidated by PREFIX
        // because this hook does not know which conversation raised the row, and the alternative is
        // threading a conversation id through every call site to clear one small list.
        await queryClient.invalidateQueries({ queryKey: ['conversation-decisions'] });
        // And the contact book, where an OUTREACH question renders (the Inbox Needs-you card, the contact
        // screen's question cards, a thread's stage card). The thread leaves Needs you only when the
        // server's stage moves, so the block keeps its settled sentence until this re-read lands — no
        // optimistic removal. EACH prefix, because the list, the person read, the recipient read and the
        // whole-book pages are four separate keys (`lib/query-client.ts`), and the row being answered can
        // be showing from any of them (`refreshOutreachStages`). Only for a question raised ON a thread.
        if (outreachId) await refreshOutreachStages();
      }
    },
    [decision, goalId, parentGoalId, taskId, outreachId, queryClient],
  );

  return { busy, answered, effect, submit };
}
